const SERVER_ACTION_RELOAD_SESSION_KEY = "server-action-reload-attempted";
const SERVER_ACTION_MISMATCH_PATTERNS = [
  "Failed to find Server Action",
  "was not found on the server",
  "failed-to-find-server-action",
];
const SERVER_ACTION_RECOVERY_LISTENER_KEY =
  "__serverActionMismatchRecoveryListenersAttached";

export type ServerActionMismatchRecoveryContext = {
  message: string;
  source: "error" | "unhandledrejection";
  hasStorageAccess: boolean;
  reason: "reload_triggered" | "already_attempted" | "storage_unavailable";
};

export type SetupServerActionMismatchRecoveryOptions = {
  onRecoveryAttempt?: (context: ServerActionMismatchRecoveryContext) => void;
  onRecoverySkipped?: (context: ServerActionMismatchRecoveryContext) => void;
};

export function setupServerActionMismatchRecovery(
  options: SetupServerActionMismatchRecoveryOptions = {},
): void {
  if (typeof window === "undefined") {
    return;
  }

  const windowWithRecoveryFlag = window as Window & {
    [SERVER_ACTION_RECOVERY_LISTENER_KEY]?: boolean;
  };

  if (windowWithRecoveryFlag[SERVER_ACTION_RECOVERY_LISTENER_KEY]) {
    return;
  }

  windowWithRecoveryFlag[SERVER_ACTION_RECOVERY_LISTENER_KEY] = true;

  window.addEventListener("unhandledrejection", (event) => {
    const didTriggerReload = _reloadForServerActionMismatch({
      message: _getErrorMessage(event.reason),
      source: "unhandledrejection",
      options,
    });

    if (didTriggerReload) {
      event.preventDefault();
    }
  });

  window.addEventListener("error", (event) => {
    _reloadForServerActionMismatch({
      message: _getErrorMessage(event.error) ?? event.message,
      source: "error",
      options,
    });
  });
}

function _reloadForServerActionMismatch(params: {
  message?: string;
  source: "error" | "unhandledrejection";
  options: SetupServerActionMismatchRecoveryOptions;
}): boolean {
  const { message, source, options } = params;

  if (!_isServerActionMismatchMessage(message)) {
    return false;
  }

  if (!message) {
    return false;
  }

  let hasStorageAccess = true;

  try {
    const reloadAlreadyAttempted =
      window.sessionStorage.getItem(SERVER_ACTION_RELOAD_SESSION_KEY) === "1";

    if (reloadAlreadyAttempted) {
      options.onRecoverySkipped?.({
        message,
        source,
        hasStorageAccess,
        reason: "already_attempted",
      });
      return false;
    }

    window.sessionStorage.setItem(SERVER_ACTION_RELOAD_SESSION_KEY, "1");
  } catch {
    // Bail out to avoid reload loops when storage is unavailable.
    hasStorageAccess = false;
    options.onRecoverySkipped?.({
      message,
      source,
      hasStorageAccess,
      reason: "storage_unavailable",
    });
    return false;
  }

  options.onRecoveryAttempt?.({
    message,
    source,
    hasStorageAccess,
    reason: "reload_triggered",
  });
  window.location.reload();
  return true;
}

function _isServerActionMismatchMessage(message?: string): boolean {
  if (!message) {
    return false;
  }

  return SERVER_ACTION_MISMATCH_PATTERNS.some((pattern) =>
    message.includes(pattern),
  );
}

function _getErrorMessage(errorLike: unknown): string | undefined {
  if (typeof errorLike === "string") {
    return errorLike;
  }

  if (
    typeof errorLike === "object" &&
    errorLike !== null &&
    "message" in errorLike
  ) {
    const message = (errorLike as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
  }

  return undefined;
}
